import { ChangeEvent, FC, useCallback, useState } from "react";
import {
  Box,
  Button,
  capitalize,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDidMount } from "rooks";
import { ColDef } from "ag-grid-community";
import { usePostAiSavingsOpportunitiesMutation } from "@/services/cloudchipr.api";
import { PoorAgGrid } from "@/storybook/poor-ag-grid/PoorAgGrid";

export const TestAiSavingOpportunities: FC = () => {
  const [trigger, { data, isLoading }] =
    usePostAiSavingsOpportunitiesMutation();
  const [inputValue, setInputValue] = useState("30");

  useDidMount(() => {
    trigger({
      body: {
        type: "all",
        days: +inputValue,
      },
    });
  });

  const aiKeys = Object.keys(data?.AI ?? {}) as any[];
  const nonAiKeys = Object.keys(data?.NON_AI ?? {}) as any[];

  const inputChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    },
    [],
  );

  const submitButtonClickHandler = useCallback(() => {
    trigger({
      body: {
        type: "all",
        days: +inputValue,
      },
    });
  }, [trigger, inputValue]);

  return (
    <Stack mt={3} spacing={1}>
      <Stack direction="row" spacing={2} px={2}>
        <TextField
          value={inputValue}
          onChange={inputChangeHandler}
          placeholder="Days Count"
          label="Days Count"
          variant="outlined"
          sx={{
            width: 200,
          }}
          type="number"
          size="small"
        />
        <Button
          onClick={submitButtonClickHandler}
          variant="contained"
          size="small"
        >
          Submit
        </Button>
      </Stack>

      {isLoading && <LinearProgress />}

      {!!aiKeys?.length && (
        <Stack spacing={1}>
          <Typography variant="body1" fontWeight="bold">
            Ai Generated
          </Typography>
          <Stack spacing={1}>
            {aiKeys.map((resourceType) => {
              if (resourceType === "KUBERNETES") {
                return (
                  <Stack spacing={1} key={resourceType}>
                    <Typography variant="body2" pl={2} fontWeight="medium">
                      {resourceType}
                    </Typography>
                    {data?.AI?.[resourceType]?.["AI"] && (
                      <Box height={400}>
                        <PoorAgGrid
                          data={data?.AI?.[resourceType]?.["AI"]}
                          colDefsModifierFn={colDefModifier}
                        />
                      </Box>
                    )}
                  </Stack>
                );
              }
              return (
                <Stack spacing={1} key={resourceType}>
                  <Typography variant="body2" pl={2} fontWeight="medium">
                    {resourceType}
                  </Typography>
                  {data?.AI?.[resourceType] && (
                    <Box height={400}>
                      <PoorAgGrid
                        data={data.AI[resourceType]}
                        colDefsModifierFn={colDefModifier}
                      />
                    </Box>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}

      {!!nonAiKeys?.length && (
        <Stack spacing={1}>
          <Typography variant="body1" fontWeight="bold">
            NON Ai Generated
          </Typography>
          <Stack spacing={1}>
            {nonAiKeys.map((resourceType) => {
              return (
                <Stack spacing={1}>
                  <Typography variant="body2" pl={2} fontWeight="medium">
                    {resourceType}
                  </Typography>
                  {data?.NON_AI?.[resourceType] && (
                    <Box height={400}>
                      <PoorAgGrid
                        data={data.NON_AI[resourceType]}
                        colDefsModifierFn={colDefModifier}
                      />
                    </Box>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const colDefModifier = (colDef: ColDef[]) => {
  const def =
    colDef?.map((def: any) => ({
      ...def,
      headerName: def?.headerName
        ?.split("_")
        ?.map((name: string) => capitalize(name))
        ?.join(" "),
    })) ?? [];

  const recommendationsIndex = def.findIndex(
    (def: any) => def.field === "recommendations",
  );

  if (recommendationsIndex !== -1) {
    const [item] = def.splice(recommendationsIndex, 1);
    def.unshift({ ...item, flex: 2.5 });
  }

  return def;
};
